import { ReactNode, useEffect, useRef, useState } from 'react';

import Slot from '@cartwhl/ui/atoms/Slot';
import useClickOutside from '@cartwhl/ui/hooks/useClickOutside';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';

const OFFSET_TOP = 132;
const OFFSET_BOTTOM = 20;

export default function SubPanel({
  id,
  children,
  subPanelKey,
  closePanel,
  elementToTrack,
}: {
  id: string;
  children: ReactNode;
  subPanelKey?: string;
  closePanel: () => void;
  elementToTrack: HTMLDivElement | HTMLButtonElement | null;
}) {
  const [yPosition, setYPosition] = useState<number | undefined>(undefined);
  const animationFrameRef = useRef<number | null>(null);
  const subpanelRef = useRef<HTMLDivElement>(null);

  const isLgMedia = useMediaQuery('(min-width: 1024px)');

  const updateYPosition = () => {
    if (elementToTrack) {
      const rect = elementToTrack.getBoundingClientRect();
      const trackY = Math.max(rect.top, OFFSET_TOP);

      const heightContent = subpanelRef.current?.offsetHeight;
      const heightAvailable = window.innerHeight - OFFSET_BOTTOM;
      if (!heightContent) return;

      const overflow = heightContent + trackY - heightAvailable;

      let y = trackY;
      if (overflow > 0) {
        y = trackY - overflow;
      }

      setYPosition(Math.max(y, OFFSET_TOP));
    }
  };
  const handleScroll = () => {
    closePanel();
  };
  const handleResize = () => {
    // If there’s already an animation frame requested, do not request another
    if (animationFrameRef.current === null) {
      animationFrameRef.current = requestAnimationFrame(() => {
        updateYPosition();
        // Reset animation frame reference after the update
        animationFrameRef.current = null;
      });
    }
  };

  useEffect(() => {
    if (subPanelKey !== id) return;
    // Set the initial position
    updateYPosition();

    const mainPanelScrollArea = document.getElementById(
      'main-panel-scroll-area',
    );

    // Add event listeners for scroll and resize, with debounced handlers
    if (subPanelKey === id) {
      mainPanelScrollArea?.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleResize);
    }
    // Cleanup on unmount
    return () => {
      mainPanelScrollArea?.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      // Cancel the animation frame if it's still pending
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [id, subPanelKey]);

  useClickOutside(subpanelRef, closePanel, {
    exceptionsRefs: [{ current: elementToTrack as HTMLElement }],
    shouldTrigger: (e) => {
      // Don't close when we operate a dropdown or click on a "Notice":
      const target = e.target as HTMLElement;
      return !target.closest('[data-state]');
    },
  });

  if (subPanelKey !== id) return null;

  return (
    <Slot elementId="sub-panel">
      <div
        className={`dark:bg-gray-90 absolute z-10 w-full bg-white transition-all lg:left-[-322px] lg:w-[330px] lg:bg-transparent lg:dark:bg-transparent ${
          yPosition === undefined
            ? 'pointer-events-none translate-x-10 opacity-0'
            : 'pointer-events-auto translate-x-0 opacity-100'
        }`}
        style={{
          top: isLgMedia ? `${yPosition}px` : 60,
          bottom: isLgMedia ? OFFSET_BOTTOM : 0,
        }}
      >
        <div
          ref={subpanelRef}
          className="dark:bg-gray-90 no-scrollbar lg:shadow-menu lg:dark:border-gray-75 max-h-full overflow-y-scroll rounded-2xl bg-white p-4 lg:dark:border-2"
        >
          {children}
        </div>
      </div>
    </Slot>
  );
}
